import type { ReactElement } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useAuth } from '../auth/AuthProvider.js';

interface NavItem {
  to: string;
  label: string;
}

const NAV_BY_ROLE: Record<string, NavItem[]> = {
  candidate: [
    { to: '/candidate', label: 'Profile' },
    { to: '/candidate/system-check', label: 'System check' },
  ],
  interviewer: [{ to: '/interviewer', label: 'Interviews' }],
  admin: [{ to: '/interviewer', label: 'Interviews' }],
};

/**
 * The chrome around every signed-in page.
 *
 * Excluded from the live session route, which needs the full viewport for
 * `SessionScreen` — see the router: that route sits outside this layout on purpose.
 */
export function AppLayout(): ReactElement {
  const { user, logout } = useAuth();
  const items = user ? (NAV_BY_ROLE[user.role] ?? []) : [];

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center justify-between gap-6 border-b border-session-border bg-session-panel px-6 py-3">
        <div className="flex items-center gap-6">
          <span className="text-sm font-semibold tracking-tight text-session-text">Proctoring</span>
          <nav className="flex items-center gap-1" aria-label="Main">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end
                className={({ isActive }) =>
                  `rounded-md px-3 py-1.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-session-panel-raised text-session-text'
                      : 'text-session-muted hover:text-session-text'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-4">
          {user ? (
            <span className="text-sm text-session-muted">
              {user.fullName}
              <span className="ml-2 text-xs text-session-dim capitalize">{user.role}</span>
            </span>
          ) : null}
          <button type="button" onClick={logout} className="btn-secondary px-3 py-1.5 text-xs">
            Sign out
          </button>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
